import { useEffect, useState } from 'react';

import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import * as gatewayService from '@frolight/services/api/gatewayService';

const useRecentTemperatureTelemetryData = (gatewayId, unitDeviceId, dryTempSensorDeviceId, wetTempSensorDeviceId) => {

  const [ loading, setLoading ] = useState(false);
  const [ data, setData ] = useState(null);
  const [ error, setError ] = useState(null);

  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();

  useEffect(() => {
    let mounted = true;

    const abortControllers = [];

    const getRecentTelemetryData = async () => {
      if (mounted) setLoading(true);

      try {
        const timeRangeEnd = Date.now();
        const timeRangeStart = timeRangeEnd - (11 * 60 * 1000);

        const [ dryTempSensorTelemetryData, wetTempSensorTelemetryData ] = await Promise.all(
          [dryTempSensorDeviceId, wetTempSensorDeviceId].map(async (sensorDeviceId) => {
            if (sensorDeviceId) {
              const abortController = new AbortController();
              abortControllers.push(abortController);
              const messageResponse = await authenticatedAPIServiceCall(
                gatewayService.getTelemetryMessagesForPeripheral,
                {
                  abortController: abortController,
                  opts: {
                    gatewayId: gatewayId,
                    unitDeviceId: unitDeviceId,
                    peripheralDeviceId: sensorDeviceId,
                    filters: {
                      dataFrom: timeRangeStart,
                      dataTo: timeRangeEnd
                    }
                  }
                }
              );

              const messages = messageResponse.data.map((msg) => {
                const temperatureData = msg.Payload?.data?.temperature;
                return {
                  timestamp: msg.DataTimestamp * 1000,
                  temperature: {
                    min: temperatureData.min,
                    max: temperatureData.max,
                    current: temperatureData.current
                  }
                };
              });

              if (messages.length === 0) {
                return;
              } else {
                return messages.sort((a, b) => b.timestamp - a.timestamp)[0];
              }
            } else {
              return;
            }
          })
        );

        if (mounted) setData({
          dry: dryTempSensorTelemetryData,
          wetbulb: wetTempSensorTelemetryData
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          if (mounted) setError(err);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (gatewayId && unitDeviceId && (dryTempSensorDeviceId || wetTempSensorDeviceId)) {
      if (mounted) setError(null);
      getRecentTelemetryData();
    }

    return () => {
      mounted = false;
      abortControllers.forEach(ac => ac.abort());
    }
  }, [ gatewayId, unitDeviceId, dryTempSensorDeviceId, wetTempSensorDeviceId ]);

  return { loading, error, data };

};

export default useRecentTemperatureTelemetryData;